import { useEffect, useState } from 'react';

import {
	Box,
	Button,
	Checkbox,
	Group,
	LoadingOverlay,
	Paper,
	Radio,
	ScrollArea,
	SimpleGrid,
	Table,
	Text,
} from '@mantine/core';

import { useQuery } from '@tanstack/react-query';

import { scudMappingApi } from '@/shared/api/scudMapping';
import { SCUD_EMPTY, SCUD_SEARCH_MIN_LENGTH } from '@/shared/constant/scudMapping';
import {
	IScudFederationEmployee,
	IScudMapping,
	TScudFederationSearchParams,
} from '@/shared/types/scudMapping.types';
import { LabelInput, Select } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';

import { formatScudCalendarDate, formatScudDate, joinParts, scudPassNumber } from './utils';

/** Бэкенд отдаёт не больше 200 человек */
const SEARCH_LIMIT = 200;

/** Режим поиска: при открытии — по фамилии из СКУД, по вводу — по части ФИО */
type TSearchText = Pick<TScudFederationSearchParams, 'query' | 'surname'>;

const isLongEnough = (value: string) => value.replace(/\s/g, '').length >= SCUD_SEARCH_MIN_LENGTH;

type FederationSearchModalProps = {
	opened: boolean;
	onClose: () => void;
	onSelect: (employee: IScudFederationEmployee) => void;
	mapping?: IScudMapping | null;
	initialQuery?: string;
};

export const FederationSearchModal = ({
	opened,
	onClose,
	onSelect,
	mapping,
	initialQuery,
}: FederationSearchModalProps) => {
	const [query, setQuery] = useState('');
	const [companyId, setCompanyId] = useState<string | null>(null);
	const [unitId, setUnitId] = useState<string | null>(null);
	const [onlyWorking, setOnlyWorking] = useState(true);
	const [params, setParams] = useState<TScudFederationSearchParams | null>(null);
	const [selectedId, setSelectedId] = useState<number | null>(null);
	const [queryError, setQueryError] = useState<string | null>(null);

	const { data: companies = [] } = useQuery({
		queryKey: ['scud_federation_companies'],
		queryFn: scudMappingApi.getCompanies,
		enabled: opened,
		staleTime: 600_000,
	});

	const { data: units = [] } = useQuery({
		queryKey: ['scud_federation_units', companyId],
		queryFn: () => scudMappingApi.getUnits(Number(companyId)),
		enabled: opened && !!companyId,
		staleTime: 600_000,
	});

	const {
		data: employees = [],
		isFetching,
		isSuccess,
	} = useQuery({
		queryKey: ['scud_federation_search', params],
		queryFn: () => scudMappingApi.searchFederation(params as TScudFederationSearchParams),
		enabled: opened && !!params,
	});

	const buildParams = (
		text: TSearchText,
		company: string | null,
		unit: string | null,
		working: boolean,
	): TScudFederationSearchParams => ({
		...text,
		company_id: company ? Number(company) : undefined,
		unit_id: unit ? Number(unit) : undefined,
		only_working: working,
	});

	useEffect(() => {
		if (!opened) {
			return;
		}

		// По фамилии бэкенд ищет без ограничения в 3 символа — короткие фамилии (Ли, Ян) находятся сразу
		const surname = (initialQuery ?? mapping?.full_name_skud ?? '').trim().split(/\s+/)[0] ?? '';

		setQuery(surname);
		setCompanyId(null);
		setUnitId(null);
		setOnlyWorking(true);
		setSelectedId(null);
		setQueryError(null);
		setParams(surname ? buildParams({ surname }, null, null, true) : null);
	}, [opened, mapping?.id, initialQuery]);

	const handleSearch = () => {
		const text = query.trim();
		// query принимается от 3 символов, а короткую фамилию (Ли, Ян) бэкенд ищет только по surname
		const isSurname =
			!!text && (text === params?.surname || (!/\s/.test(text) && !isLongEnough(text)));

		if (isSurname) {
			setQueryError(null);
			setSelectedId(null);
			setParams(buildParams({ surname: text }, companyId, unitId, onlyWorking));
			return;
		}

		if (!isLongEnough(query)) {
			setQueryError(`Введите не менее ${SCUD_SEARCH_MIN_LENGTH} символов`);
			return;
		}

		setQueryError(null);
		setSelectedId(null);
		setParams(buildParams({ query: query.trim() }, companyId, unitId, onlyWorking));
	};

	// Уточнение сохраняет текущий режим поиска: по фамилии или по части ФИО
	const refine = (company: string | null, unit: string | null, working: boolean) => {
		if (params) {
			setSelectedId(null);
			setParams(
				buildParams({ query: params.query, surname: params.surname }, company, unit, working),
			);
		}
	};

	const selected = employees.find((employee) => employee.person_id === selectedId) ?? null;

	const handleSelect = () => {
		if (selected) {
			onSelect(selected);
		}
	};

	const rows = employees.map((employee) => {
		const isSelected = employee.person_id === selectedId;

		return (
			<Table.Tr
				key={employee.person_id}
				bg={isSelected ? 'var(--mantine-color-blue-light)' : undefined}
				style={{ cursor: 'pointer' }}
				onClick={() => setSelectedId(employee.person_id)}
				onDoubleClick={() => onSelect(employee)}
			>
				<Table.Td w={40}>
					<Radio
						checked={isSelected}
						onChange={() => setSelectedId(employee.person_id)}
						size="sm"
					/>
				</Table.Td>
				<Table.Td>
					<Text size="sm" fw={600}>
						{employee.full_name}
					</Text>
				</Table.Td>
				<Table.Td>{employee.company_name ?? SCUD_EMPTY}</Table.Td>
				<Table.Td>{employee.unit_name ?? SCUD_EMPTY}</Table.Td>
				<Table.Td>{employee.position ?? SCUD_EMPTY}</Table.Td>
				<Table.Td>{employee.tab_number ?? SCUD_EMPTY}</Table.Td>
				<Table.Td>{employee.email ?? SCUD_EMPTY}</Table.Td>
				<Table.Td>{formatScudCalendarDate(employee.in_date)}</Table.Td>
				<Table.Td>{formatScudCalendarDate(employee.out_date)}</Table.Td>
			</Table.Tr>
		);
	});

	return (
		<ModalSheet
			title="Найти сотрудника в «Федерации»"
			opened={opened}
			onClose={onClose}
			size="80rem"
			zIndex={310}
			scrollAreaComponent={ScrollArea.Autosize}
		>
			<Text size="sm" c="dimmed">
				Выберите сотрудника, которому принадлежит пропуск
			</Text>
			{mapping && (
				<Paper withBorder radius="lg" p="sm">
					<SimpleGrid cols={{ base: 2, md: 5 }} spacing="sm">
						<InfoItem label="ФИО из СКУД" value={mapping.full_name_skud} />
						<InfoItem label="Фирма (СКУД)" value={mapping.skud_company} />
						<InfoItem
							label="Подразделение · должность (СКУД)"
							value={joinParts(mapping.skud_department, mapping.skud_post)}
						/>
						<InfoItem label="Пропуск" value={scudPassNumber(mapping)} />
						<InfoItem label="Последний проход" value={formatScudDate(mapping.last_pass)} />
					</SimpleGrid>
				</Paper>
			)}
			<Group align="flex-end" gap="sm">
				<LabelInput
					compact
					label={`ФИО (от ${SCUD_SEARCH_MIN_LENGTH} символов)`}
					miw={280}
					style={{ flex: 1 }}
					value={query}
					error={queryError}
					onChange={(event) => setQuery(event.currentTarget.value)}
					clearable
					onClear={() => setQuery('')}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							event.preventDefault();
							handleSearch();
						}
					}}
					data-autofocus
				/>
				<Select
					compact
					caption="Компания"
					placeholder="Все"
					w={240}
					data={companies.map((company) => ({ value: `${company.id}`, label: company.name }))}
					value={companyId}
					onChange={(value) => {
						setCompanyId(value);
						setUnitId(null);
						refine(value, null, onlyWorking);
					}}
					comboboxProps={{ zIndex: 320 }}
					searchable
					clearable
				/>
				<Select
					compact
					caption="Подразделение"
					placeholder="Все"
					w={240}
					data={units.map((unit) => ({ value: `${unit.id}`, label: unit.name }))}
					value={unitId}
					onChange={(value) => {
						setUnitId(value);
						refine(companyId, value, onlyWorking);
					}}
					disabled={!companyId}
					comboboxProps={{ zIndex: 320 }}
					searchable
					clearable
				/>
				<Button h={42} onClick={handleSearch}>
					Найти
				</Button>
			</Group>
			<Checkbox
				label="Только работающие"
				checked={onlyWorking}
				onChange={(event) => {
					setOnlyWorking(event.currentTarget.checked);
					refine(companyId, unitId, event.currentTarget.checked);
				}}
			/>
			<Box pos="relative" mih={120}>
				<LoadingOverlay visible={isFetching} zIndex={10} overlayProps={{ blur: 1 }} />
				{params && isSuccess && (
					<Text size="sm" fw={600} mb={4}>
						Результаты поиска ({employees.length})
						{employees.length >= SEARCH_LIMIT && (
							<Text span size="sm" c="dimmed" fw={400}>
								{' '}
								— показаны первые {SEARCH_LIMIT}, уточните запрос
							</Text>
						)}
					</Text>
				)}
				{params && isSuccess && !employees.length ? (
					<Text c="dimmed" ta="center" py="lg">
						Сотрудники не найдены. Проверьте ФИО или измените запрос.
					</Text>
				) : (
					<ScrollArea.Autosize mah="45vh">
						<Table highlightOnHover stickyHeader verticalSpacing="xs">
							<Table.Thead>
								<Table.Tr>
									<Table.Th />
									<Table.Th>ФИО</Table.Th>
									<Table.Th>Компания</Table.Th>
									<Table.Th>Подразделение</Table.Th>
									<Table.Th>Должность</Table.Th>
									<Table.Th>Таб. №</Table.Th>
									<Table.Th>E-mail</Table.Th>
									<Table.Th>Принят</Table.Th>
									<Table.Th>Уволен</Table.Th>
								</Table.Tr>
							</Table.Thead>
							<Table.Tbody>{rows}</Table.Tbody>
						</Table>
					</ScrollArea.Autosize>
				)}
			</Box>
			<Group justify="flex-end" gap="sm">
				<Button variant="outline" onClick={onClose}>
					Отмена
				</Button>
				<Button disabled={!selected} onClick={handleSelect}>
					Выбрать
				</Button>
			</Group>
		</ModalSheet>
	);
};

export const InfoItem = ({ label, value }: { label: string; value: string | null | undefined }) => (
	<Box>
		<Text size="xs" c="dimmed">
			{label}
		</Text>
		<Text size="sm" fw={600}>
			{value || SCUD_EMPTY}
		</Text>
	</Box>
);
